import { Localized } from "@fluent/react"
import { Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, MenuItem, Skeleton, Stack, Switch, TextField, Tooltip, Typography } from "@mui/material"
import { LoadingButton } from "@mui/lab"
import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react"
import { AppTextFieldHandler } from "../../../app/appTypes"
import { useAppDispatch, useAppSelector, useDebouncedValue } from "../../../app/hooks"
import { URSRequirement, updateURSRequirement, UrsGroup, UrsRequirementType } from "../UrsSlice"
import { RichTextEditor } from "../../../app/RichTextEditor"
import { URSComment } from "../URSCommentSlice"
import { Comments } from "../Comments"
import { If } from "../../../app/If"
import { Check, Clear, FilterAlt, NavigateBefore, NavigateNext, QuestionMark } from "@mui/icons-material";
import { SaveButton } from "../../../app/components/SaveButton"
import { CancelButton } from "../../../app/components/CancelButton"
import { DeleteButton } from "../../../app/components/DeleteButton"
import { DeleteRequirementDialog } from "./DeleteRequirementDialog"
import { showSuccess } from "../../notifications/notificationsSlice"
import { RequirementNote } from "../../ursOffer/ursOfferSlice"
import { DiscussOld, Comment } from "../../../app/Discuss"
import { selectLoggedUserId } from "../../user/userSlice"
import { parseISODate } from "../../../app/Utils"
import { DocumentNumberBox } from "../../../app/components/DocumentNumberBox"
import SmallStatusBadge from "../../../app/components/SmallStatusBadge"
import { alpha } from "@mui/material/styles"

// Split out so that node-htmldiff only reaches the browser once an edit is actually
// made — see the `nameChanged` guard below.
const HtmlDiff = lazy(() => import("../../../app/HtmlDiff"))

// Fixed, and the comments column is always reserved: the pager swaps requirements
// without closing the dialog, so sizing either of them off the presence of comments
// made the layout jump on every move.
const DIALOG_WIDTH = 'xl'

// Same reasoning for the height: requirement text and comment lists vary wildly in
// length, so a content-sized dialog resized — and moved its action buttons — on
// every step of the pager. The body scrolls inside a frame that never moves.
const DIALOG_HEIGHT = '90vh'

// Supplied by panels that can walk their requirement list; omitting it hides the
// pager entirely. `prev`/`next` are null at the ends of the list.
export interface EditRequirementNavigation {
    prev: (() => void) | null
    next: (() => void) | null
    index: number // 1-based position within the set the pager walks
    total: number
    // The pager is walking a filtered subset of the document. Worth surfacing:
    // without it, Next skipping over requirements reads as a bug.
    filtered?: boolean
}

export interface EditRequirementDialogProps {
    requirement: URSRequirement
    requirementGroup: UrsGroup | null
    // No onSuccess counterpart: saving leaves the dialog open, so the only way out
    // is onCancel.
    onCancel: () => void
    rootSection: UrsRequirementType
    comments: URSComment[]
    canSendComments: () => boolean
    canResolveComments: () => boolean
    canEditComment: (comment: URSComment) => boolean
    isOfferReady: boolean
    navigation?: EditRequirementNavigation
    // Mirrors the table's own delete affordance, which is gated on the save link.
    // Omitted means no delete button, so callers that never allowed it are unchanged.
    canDelete?: boolean
}

// TODO: add comments and attachments
export const EditRequirementDialog = (props: EditRequirementDialogProps) => {
    const { requirement, onCancel, comments, isOfferReady, navigation } = props
    const { canSendComments, canResolveComments } = props
    const dispatch = useAppDispatch()
    // SELECTORS 
    const entity = requirement
    const { criticality: defCriticality, typeId: defTypeId, name: defName, name_orig: defNameOrig, group: defGroup, order: defOrder, note: defNote, comment: defComment } = entity
    const currentUserId = useAppSelector(selectLoggedUserId)

    // STATE
    const [criticality, setCriticality] = useState(defCriticality)
    const [name, setName] = useState(defName)
    // Frozen until the dialog is pointed at another requirement: the parent re-renders
    // with a fresh Urs whenever anything in the document is saved, so `defNameOrig` is
    // not a reliable baseline for the dialog's lifetime.
    const [originalName, setOriginalName] = useState(defNameOrig)
    const [saving, setSaving] = useState(false)
    const [note, setNote] = useState<RequirementNote | null>(defNote)
    const [comment, setComment] = useState<string | null>(defComment)
    const [comparisonView, setComparisonView] = useState<"changes" | "previous">("changes")

    // What the server last accepted. Saving no longer closes the dialog, and the parent
    // keeps handing back the snapshot it captured when the dialog opened, so the props
    // cannot serve as the dirty-check baseline past the first save.
    const [saved, setSaved] = useState({ name: defName, criticality: defCriticality, note: defNote, comment: defComment })

    // The pager swaps the requirement under a dialog that stays mounted — keying the
    // whole dialog would replay MUI's exit/enter transition and read as a reopen. So
    // reset during render instead, the way a remount would have.
    const [shownId, setShownId] = useState(entity.id)
    if (shownId !== entity.id) {
        setShownId(entity.id)
        setCriticality(defCriticality)
        setName(defName)
        setOriginalName(defNameOrig)
        setNote(defNote)
        setComment(defComment)
        setSaved({ name: defName, criticality: defCriticality, note: defNote, comment: defComment })
    }

    // entity.id doubles as the reset key so the debounced value does not keep trailing
    // the previous requirement's name for one interval after the pager moves.
    // The setters above only take effect on the re-render they schedule, so during *this*
    // pass `name` is still the previous requirement's text while `entity.id` is already
    // the new one. Feeding that pair to the hook would reset it to the wrong value — the
    // props are the only trustworthy source until the state catches up.
    const debouncedName = useDebouncedValue(shownId === entity.id ? name : defName, 300, entity.id)
    const nameChanged = debouncedName !== originalName
    const canShowComparison = nameChanged && entity.status !== "ADDED" && entity.status !== "DELETED"

    // The table hides its Edit action on deleted requirements, but the pager can still
    // land on one — they stay in the document after an OPINION snapshot, and in the
    // supplier flow, precisely so a reviewer can see what was removed. So this is a
    // viewer, not an editor, for them: readable, commentable, not writable.
    const isDeleted = entity.status === 'DELETED'
    const deletionReason = entity.deletionReason?.trim() || entity.deleteReason?.trim()

    // Children holding their own state still need remounting: tiptap only reads
    // `content` on creation, and Comments would carry a draft over to the next
    // requirement.
    const requirementKey = entity.id

    // The dialog body no longer unmounts between requirements, so its scroll offset
    // would otherwise persist onto the next one.
    const contentRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
        contentRef.current?.scrollTo({ top: 0 })
    }, [requirementKey])

    // HANDLERS 
    const handleCriticalityChange: AppTextFieldHandler = useCallback(e => setCriticality(e.target.value), [setCriticality])
    const dirty = name !== saved.name
        || criticality !== saved.criticality
        || note !== saved.note
        || comment !== saved.comment

    const save = async () => {
        await dispatch(updateURSRequirement({
            ...entity,
            name,
            criticality,
            sectionGuid: entity.sectionGuid,
            note,
            comment,
        })).unwrap()
        setSaved({ name, criticality, note, comment })
    }

    // Saving keeps the dialog open — the requirement list behind it updates on its own,
    // and the Save button going quiet is the signal that the write landed.
    const handleOkClick = async () => {
        setSaving(true)
        try {
            await save()
        } catch (err) {
            console.error(err)
        } finally {
            setSaving(false)
        }
    }

    // Closing is the only path that can drop an edit — the pager saves on its way out
    // and Save keeps the dialog open — so the guard sits here alone.
    const [confirmClose, setConfirmClose] = useState(false)

    const handleCloseClick = () => {
        if (dirty) {
            setConfirmClose(true)
        } else {
            onCancel()
        }
    }

    const handleSaveAndClose = async () => {
        setSaving(true)
        try {
            await save()
            onCancel()
        } catch (err) {
            console.error(err)
            setConfirmClose(false) // back to the editor rather than out with the edit lost
        } finally {
            setSaving(false)
        }
    }

    // Deleting is soft on the server, but it also negates the requirement's order and
    // the view builder only returns positive ones — so in the ordinary case the
    // requirement drops out of the document and the pager collapses with it. The
    // successor therefore has to be captured while the pager can still name it, and
    // the user moved there once the delete lands. Staying put would work only in the
    // post-opinion and supplier flows, where deleted requirements remain visible.
    const [confirmDelete, setConfirmDelete] = useState(false)
    const nextAfterDelete = useRef<(() => void) | null>(null)

    const handleDeleteClick = () => {
        nextAfterDelete.current = navigation?.next ?? null
        setConfirmDelete(true)
    }

    const handleDeleted = () => {
        setConfirmDelete(false)
        // Whatever was in the editor describes a requirement that no longer exists;
        // leaving the dirty flag set would make Close ask about saving it.
        setSaved({ name, criticality, note, comment })
        const go = nextAfterDelete.current
        nextAfterDelete.current = null
        if (go === null) {
            onCancel()
            return
        }
        dispatch(showSuccess('urs-requirement-deleted-next'))
        go()
    }

    // Save-then-move. A rejected save leaves the dialog on the current requirement so
    // the pending edit is not silently dropped.
    const handleNavigate = async (go: () => void) => {
        setSaving(true)
        try {
            if (dirty) {
                await save()
            }
            go()
        } catch (err) {
            console.error(err)
        } finally {
            setSaving(false)
        }
    }

    const discussion: Comment[] = requirement.offerMessages.map(m => {
        return {
            someId: m.id,
            content: m.message,
            author: m.author,
            parentId: m.parentMessageId,
            authorId: m.userId,
            companyName: m.companyName,
            date: parseISODate(m.date),
            guid: m.guid,
            kind: m.type,
        }
    })

    return <>
        <Dialog
            open={true}
            fullWidth
            maxWidth={DIALOG_WIDTH}
            PaperProps={{ sx: { height: DIALOG_HEIGHT } }}
        >
            <DialogTitle>
                {isDeleted
                    ? <Localized id="requirements-view-deleted"><span>Deleted requirement</span></Localized>
                    : <Localized id="requirements-edit"><span>Edycja wymagania</span></Localized>}
            </DialogTitle>
            <DialogContent dividers ref={contentRef}>
                <Grid container spacing={4}>
                    <Grid item xs={7}>
                        <Stack direction="column" spacing={2}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: 1,
                                    backgroundColor: 'grey.100',
                                    border: theme => `1px solid ${theme.palette.divider}`,
                                }}
                            >

                                <DocumentNumberBox value={entity.numeration} />
                                <SmallStatusBadge label={entity.status} />

                            </Box>
                            {isDeleted && deletionReason && (
                                <Typography variant="body2" color="error" sx={{ fontStyle: 'italic' }}>
                                    <Localized id="delete-requirement-reason-display" vars={{ reason: deletionReason }}>
                                        <span>Deletion reason: {deletionReason}</span>
                                    </Localized>
                                </Typography>
                            )}
                            <TextField
                                value={criticality}
                                fullWidth
                                required={true}
                                select
                                disabled={isDeleted}
                                label={<Localized id="urs-item-criticality"><span>Criticality</span></Localized>}
                                onChange={handleCriticalityChange}
                            >

                                <MenuItem value={"E"}><Localized id="urs-item-criticality-e"><span>Essential</span></Localized></MenuItem>
                                <MenuItem value={"D"}><Localized id="urs-item-criticality-d"><span>Desired</span></Localized></MenuItem>
                                <MenuItem value={"I"}><Localized id="urs-item-criticality-i"><span>Optional</span></Localized></MenuItem>
                            </TextField>
                            <RichTextEditor key={requirementKey} label={null} value={name} onValueChange={setName} editable={!isDeleted} />
                            {canShowComparison && <>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mt: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography
                                            variant="body2"
                                            color={comparisonView === "changes" ? "text.primary" : "text.secondary"}
                                            sx={{ fontWeight: comparisonView === "changes" ? 600 : 400 }}
                                        >
                                            <Localized id="urs-requirement-name-diff">
                                                <span>Zmiany</span>
                                            </Localized>
                                        </Typography>
                                        <Switch
                                            size="small"
                                            checked={comparisonView === "previous"}
                                            onChange={(_, checked) => setComparisonView(checked ? "previous" : "changes")}
                                        />
                                        <Typography
                                            variant="body2"
                                            color={comparisonView === "previous" ? "text.primary" : "text.secondary"}
                                            sx={{ fontWeight: comparisonView === "previous" ? 600 : 400 }}
                                        >
                                            <Localized id="urs-requirement-previous-content">
                                                <span>Poprzednia treść</span>
                                            </Localized>
                                        </Typography>
                                    </Box>
                                </Box>

                                {comparisonView === "changes" ? (
                                    <Suspense fallback={<Skeleton variant="rectangular" height={80} />}>
                                        <HtmlDiff
                                            before={originalName}
                                            after={debouncedName}
                                            label={<Localized id="urs-requirement-name-diff"><span>Zmiany</span></Localized>}

                                        />
                                    </Suspense>
                                ) : (
                                    <Box
                                        sx={{
                                            border: theme => `1px solid ${theme.palette.divider}`,
                                            borderRadius: 1,
                                            p: 2,
                                            maxHeight: '40vh',
                                            overflowY: 'auto',
                                            overflowWrap: 'break-word',
                                            backgroundColor: theme => alpha(theme.palette.action.hover, 0.1),

                                            ['& p']: { margin: 0 },
                                            ['& p + p']: { marginTop: 1 },
                                            ['& img']: { maxWidth: '100%' },
                                        }}
                                    >
                                        <div className="tiptap" dangerouslySetInnerHTML={{ __html: originalName ?? "" }}></div>
                                    </Box>
                                )}
                            </>}
                            <If condition={isOfferReady}>
                                <Card>
                                    <CardContent>
                                        <Stack direction="column" spacing={2}>
                                            <h4><Localized id='urs-supplier-comment'>Komentarz dostawcy</Localized></h4>
                                            <TextField
                                                sx={{ maxWidth: '400px' }}
                                                value={note ?? ''}
                                                fullWidth
                                                disabled={true}
                                                select
                                                label={<Localized id="urs-offer-req-opinion"><span>Ocena</span></Localized>}
                                                onChange={e => setNote(e.target.value as RequirementNote)}
                                            >
                                                <MenuItem value={"F"}><Check fontSize="small" color='success' /> <Localized id="urs-offer-req-f"><span> Spełnia</span></Localized></MenuItem>
                                                <MenuItem value={"U"}><Clear fontSize="small" color='error' /><Localized id="urs-offer-req-u"><span> Nie spełnia</span></Localized></MenuItem>
                                                <MenuItem value={"D"}><QuestionMark fontSize="small" color='warning' /><Localized id="urs-offer-req-d"><span> Do oceny</span></Localized></MenuItem>

                                            </TextField>
                                            <TextField
                                                fullWidth
                                                multiline
                                                disabled={true}
                                                minRows={4}
                                                inputProps={{ maxLength: 1000 }}
                                                value={comment ?? ''}
                                                onChange={e => setComment(e.target.value)}
                                                label={<Localized id="urs-offer-req-comment"><span>Komentarz</span></Localized>}
                                            />
                                            <If condition={discussion.length > 0}>
                                                <DiscussOld
                                                    allComments={discussion}
                                                    postComment={null}
                                                    updateComment={null}
                                                    deleteComment={null}
                                                    currentUserId={currentUserId}
                                                    showTitle={true}
                                                    readOnly={true}
                                                />
                                            </If>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </If>
                        </Stack>
                    </Grid>
                    <Grid item xs={5}>
                        <Comments
                            key={requirementKey}
                            ursId={entity.ursId}
                            comments={comments}
                            requirement={entity}
                            isCommentEnabled={canSendComments}
                            isResolvingEnabled={canResolveComments}
                            isEditEnabled={props.canEditComment}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                {/* Destructive, so it leads on the far left with a spacer between it and
                    everything else, rather than sitting among the buttons you press
                    routinely. Deleted requirements are skipped for the same reason the
                    table hides its delete action on them — the pager can still land on one. */}
                {props.canDelete && entity.status !== 'DELETED' && <>
                    <DeleteButton loading={saving} onClick={handleDeleteClick} />
                    <Box sx={{ flex: 1 }} />
                </>}
                {/* index 0 means the requirement is not in the document any more — no pager */}
                {navigation !== undefined && navigation.index > 0 && <>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<NavigateBefore />}
                        disabled={saving || navigation.prev === null}
                        onClick={() => handleNavigate(navigation.prev!)}
                    >
                        <Localized id="urs-requirement-prev"><span>Poprzednie</span></Localized>
                    </Button>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {navigation.index} / {navigation.total}
                    </Typography>
                    {navigation.filtered && <Tooltip title={
                        <Localized id="urs-filter-pager-hint">
                            <span>Navigation follows the filter set on the requirements list.</span>
                        </Localized>
                    }>
                        <Chip
                            size="small"
                            variant="outlined"
                            color="primary"
                            icon={<FilterAlt fontSize="small" />}
                            label={<Localized id="urs-filter-pager-chip"><span>Filtered</span></Localized>}
                        />
                    </Tooltip>}
                    <Button
                        size="small"
                        variant="outlined"
                        endIcon={<NavigateNext />}
                        disabled={saving || navigation.next === null}
                        onClick={() => handleNavigate(navigation.next!)}
                    >
                        <Localized id="urs-requirement-next"><span>Następne</span></Localized>
                    </Button>
                </>}
                {/* Kept in place rather than dropped for a deleted requirement: Save sits
                    between the pager and Close, so removing it slid the Next button
                    sideways as you paged onto a deleted item. Disabled says the same
                    thing without moving anything. */}
                <SaveButton loading={saving} disabled={!dirty || isDeleted} onClick={handleOkClick} />
                <CancelButton
                    loading={saving}
                    onClick={handleCloseClick}
                    label={<Localized id="close"><span>Zamknij</span></Localized>}
                />
            </DialogActions>
        </Dialog>
        {/* `navigation.next` is still live here — the delete has not happened yet — so
            it is the right source for which outcome to promise. */}
        {confirmDelete && <DeleteRequirementDialog
            entity={entity}
            typeId={entity.typeId ?? ''}
            group={entity.group ?? ''}
            onSuccess={handleDeleted}
            onCancel={() => setConfirmDelete(false)}
            hint={navigation?.next != null
                ? <Localized id="urs-requirement-delete-advances">
                    <span>After deleting, the next requirement opens automatically.</span>
                </Localized>
                : <Localized id="urs-requirement-delete-closes">
                    <span>This is the last requirement — the editor closes after deleting.</span>
                </Localized>}
        />}
        {confirmClose && <Dialog open={true} fullWidth maxWidth="xs">
            <DialogContent>
                <DialogContentText>
                    <Localized id="urs-requirement-unsaved">
                        <span>Wymaganie ma niezapisane zmiany.</span>
                    </Localized>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <LoadingButton loading={saving} size="small" variant="contained" color="success" onClick={handleSaveAndClose}>
                    <Localized id="urs-requirement-save-and-close"><span>Zapisz i zamknij</span></Localized>
                </LoadingButton>
                <LoadingButton loading={saving} size="small" color="error" onClick={onCancel}>
                    <Localized id="urs-requirement-discard"><span>Odrzuć zmiany</span></Localized>
                </LoadingButton>
                <LoadingButton loading={saving} size="small" variant="outlined" onClick={() => setConfirmClose(false)}>
                    <Localized id="urs-requirement-keep-editing"><span>Wróć do edycji</span></Localized>
                </LoadingButton>
            </DialogActions>
        </Dialog>}
    </>
}

